import { isEmpty } from 'lodash';

/**
 * Space-free labels (a pasted URL, an id) longer than this are middle-truncated when
 * rendered. Prose labels are never truncated - they wrap like any other text.
 */
export const MAX_LINK_LABEL_LENGTH = 48;

/**
 * Schemes an inline markdown link may use. Anything else (`javascript:`, `data:`, …)
 * is refused and the source is left as literal text - message content is authored by
 * users and by the Ask agent, so an unvetted href is an injection vector.
 */
const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

export interface InlineLink {
  /** Raw label source, still containing any token placeholders. */
  label: string;
  /** Raw target source, still containing any token placeholders. */
  href: string;
  /** Index just past the closing `)`, i.e. where scanning should resume. */
  end: number;
}

/**
 * Parses a `[label](href)` link starting at `start`, or returns null when the text
 * there isn't one (a bare `[`, an unclosed bracket, a line break inside either half).
 * Callers fall back to emitting the characters literally, so anything this rejects
 * still renders exactly as it was written.
 *
 * Brackets nest (so a label may contain `[…]`) and parens are balanced, which keeps
 * links to URLs that themselves contain parens intact.
 */
export function parseInlineLinkAt(text: string, start: number): InlineLink | null {
  if (text[start] !== '[') {
    return null;
  }

  let labelEnd = start + 1;
  let brackets = 1;
  while (labelEnd < text.length && text[labelEnd] !== '\n') {
    if (text[labelEnd] === '[') {
      brackets += 1;
    } else if (text[labelEnd] === ']') {
      brackets -= 1;
      if (brackets === 0) {
        break;
      }
    }
    labelEnd += 1;
  }
  if (brackets !== 0 || text[labelEnd] !== ']' || text[labelEnd + 1] !== '(') {
    return null;
  }

  let hrefEnd = labelEnd + 2;
  let parens = 1;
  while (hrefEnd < text.length && text[hrefEnd] !== '\n') {
    if (text[hrefEnd] === '(') {
      parens += 1;
    } else if (text[hrefEnd] === ')') {
      parens -= 1;
      if (parens === 0) {
        break;
      }
    }
    hrefEnd += 1;
  }
  if (parens !== 0 || text[hrefEnd] !== ')') {
    return null;
  }

  return {
    label: text.slice(start + 1, labelEnd),
    href: text.slice(labelEnd + 2, hrefEnd),
    end: hrefEnd + 1,
  };
}

/**
 * Returns the href to link to, or null when it isn't safe/unambiguous to linkify.
 * Root-relative paths are allowed so in-app links (e.g. a card URL) navigate through
 * the router; a scheme must be on `ALLOWED_SCHEMES`; a schemeless `example.com` is
 * left as text rather than guessed at.
 */
export function sanitizeLinkHref(href: string): string | null {
  const trimmed = href.trim();
  if (isEmpty(trimmed)) {
    return null;
  }
  // Protocol-relative (`//host`) is excluded along with everything else schemeless.
  if (trimmed.startsWith('/')) {
    return trimmed.startsWith('//') ? null : trimmed;
  }
  const scheme = SCHEME.exec(trimmed);
  if (!scheme) {
    return null;
  }
  return ALLOWED_SCHEMES.has(`${scheme[1].toLowerCase()}:`) ? trimmed : null;
}

/**
 * Middle-truncates a long, space-free label - a URL used as its own label, which is
 * what an agent produces when it ignores "give the link a short name". Truncating in
 * the middle keeps both the host and the tail (ids, params) legible; the full target
 * is still the href.
 */
export function truncateLinkLabel(label: string, maxLength = MAX_LINK_LABEL_LENGTH): string {
  if (label.length <= maxLength || /\s/.test(label)) {
    return label;
  }
  const head = Math.ceil((maxLength - 1) * 0.7);
  const tail = maxLength - 1 - head;
  return `${label.slice(0, head)}…${label.slice(label.length - tail)}`;
}
